import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ATTIVITA_TIPI } from "@/lib/constants";
import { Trash2, Send } from "lucide-react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { PartecipantiField } from "@/components/PartecipantiField";

const empty = { titolo: "", tipo: "Servizio Foto", data: "", ora: "", stato: "da_fare", note: "", ssdSel: "", luogo: "", durata_min: 60, partecipanti: [] };

export function AttivitaDialog({ open, onOpenChange, onSave, onDelete, initial, fixedSsd, options = [], defaultDate, canInvite }) {
  const [form, setForm] = useState(empty);
  const { collaboratori } = useCrm();
  const { isTitolare, user } = useAuth();
  const mayInvite = isTitolare || !initial?.id || initial?.created_by === user?.id;

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { ...empty, ...initial, ssdSel: `${initial.ssd_tipo}:${initial.ssd_id}` }
        : { ...empty, data: defaultDate || "", partecipanti: fixedSsd?.email ? [{ tipo: "esterno", nome: fixedSsd.nome, email: fixedSsd.email.trim().toLowerCase() }] : [] });
    }
  }, [open, initial, defaultDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (invite = false) => {
    if (!form.titolo.trim() || !form.data) return;
    const base = { titolo: form.titolo, tipo: form.tipo, data: form.data, ora: form.ora || null, stato: form.stato, note: form.note,
      luogo: form.luogo || "", durata_min: Number(form.durata_min) || 60, partecipanti: form.partecipanti || [] };
    const opts = { invite };
    if (fixedSsd) {
      onSave(base, opts);
    } else {
      if (!form.ssdSel) return;
      const [tipo, id] = form.ssdSel.split(":");
      if (tipo === "team") {
        onSave({ ...base, ssd_id: "team", ssd_tipo: "team", ssd_nome: "Team / Interna" }, opts);
      } else {
        const opt = options.find((o) => o.id === id && o.tipo === tipo);
        onSave({ ...base, ssd_id: id, ssd_tipo: tipo, ssd_nome: opt?.nome || "" }, opts);
      }
    }
  };
  const withEmail = (form.partecipanti || []).filter((p) => p.email).length;

  const portfolioOpts = options.filter((o) => o.tipo === "portfolio");
  const pipelineOpts = options.filter((o) => o.tipo === "pipeline");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="attivita-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifica attività" : "Nuova attività"}</DialogTitle>
          <DialogDescription>{fixedSsd ? fixedSsd.nome : "Pianifica un'attività per una società."}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-1">
          {!fixedSsd && (
            <div className="grid gap-1.5">
              <Label>Società / Team</Label>
              <Select value={form.ssdSel} onValueChange={(v) => set("ssdSel", v)}>
                <SelectTrigger data-testid="attivita-ssd-select"><SelectValue placeholder="Seleziona società o attività team" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Interna / Team</SelectLabel>
                    <SelectItem value="team:team" data-testid="attivita-team-option">Riunione / Attività interna (Team)</SelectItem>
                  </SelectGroup>
                  {portfolioOpts.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Portfolio</SelectLabel>
                      {portfolioOpts.map((o) => <SelectItem key={`portfolio:${o.id}`} value={`portfolio:${o.id}`}>{o.nome}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {pipelineOpts.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Pipeline</SelectLabel>
                      {pipelineOpts.map((o) => <SelectItem key={`pipeline:${o.id}`} value={`pipeline:${o.id}`}>{o.nome}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-1.5">
            <Label>Titolo *</Label>
            <Input data-testid="attivita-titolo-input" value={form.titolo} onChange={(e) => set("titolo", e.target.value)} placeholder="es. Servizio foto squadra" />
          </div>

          <div className="grid gap-1.5">
            <Label>Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ATTIVITA_TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Data *</Label>
              <Input data-testid="attivita-data-input" type="date" value={form.data} onChange={(e) => set("data", e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Ora</Label>
              <Input data-testid="attivita-ora-input" type="time" value={form.ora || ""} onChange={(e) => set("ora", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5 col-span-2">
              <Label>Luogo</Label>
              <Input data-testid="attivita-luogo-input" value={form.luogo || ""} onChange={(e) => set("luogo", e.target.value)} placeholder="es. Palazzetto, Studio, Google Meet" />
            </div>
            <div className="grid gap-1.5">
              <Label>Durata (min)</Label>
              <Input data-testid="attivita-durata-input" type="number" min="15" step="15" value={form.durata_min} onChange={(e) => set("durata_min", e.target.value)} />
            </div>
          </div>

          <PartecipantiField value={form.partecipanti || []} onChange={(v) => set("partecipanti", v)} collaboratori={collaboratori} invited={!!initial?.invito_inviato_at} />
          {initial?.invito_inviato_at && (
            <p data-testid="attivita-invito-info" className="text-[11px] text-emerald-600 -mt-2">Invito inviato il {new Date(initial.invito_inviato_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} · reinvialo dopo una modifica per aggiornare i calendari</p>
          )}

          <div className="grid gap-1.5">
            <Label>Stato</Label>
            <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="da_fare">Da fare</SelectItem>
                <SelectItem value="fatto">Fatto</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>Note</Label>
            <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {initial?.id && onDelete && (
            <Button data-testid="attivita-delete-button" variant="ghost" className="sm:mr-auto text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5" onClick={() => onDelete(initial)}>
              <Trash2 className="h-4 w-4" /> Elimina evento
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          {canInvite && mayInvite && withEmail > 0 && (
            <Button data-testid="attivita-invite-button" variant="secondary" className="gap-1.5" onClick={() => submit(true)}>
              <Send className="h-4 w-4" /> {initial?.invito_inviato_at ? "Salva e reinvia invito" : "Salva e invia invito"} ({withEmail})
            </Button>
          )}
          <Button data-testid="attivita-submit-button" onClick={() => submit(false)}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
